<template>
  <div class="ModifyInsuranceModal">
    <v-form
      ref="$form"
      validate-on="input lazy"
    >
      <ForWhomSelectorComponent
        v-model:forWhom="forWhom"
        v-model:forWhomPeople="forWhomPeople"
        :max-people="configurations.maxInsurances"
        :isPymes="configurations.isPymes"
      />

      <BaseSelect
        v-if="isModify"
        id="insuranceType"
        :label="t('rater.health.insurance-type.form-legend')"
        :items="insuranceOptions"
        v-model="insuranceType"
        :rules="[required]"
      />

      <BaseSelect
        v-if="isModify && insuranceType === '3'"
        id="abroadOption"
        :label="t('rater.health.abroad-options.form-legend')"
        :items="abroadOptions"
        v-model="abroadType"
        :rules="[required]"
      />

      <CoverageRadioGroup
        v-if="insuranceType === '3'"
        id="coverages"
        :label="t('rater.health.coverages.label')"
        :coverages="coverageOptions"
        :value="coverage"
        v-model="coverage"
        :validation-rules="[required]"
      />

      <DatePickerComponent
        id="start-date"
        :label="t('rater.common.modify-search-view.start-date')"
        v-model="newAnswers.startDate"
        :datePickerVisible="!isModify"
        :min-date="minDateInit"
        :max-date="maxDateInit"
        :rules="[required]"
      />

      <InsuredInfoComponent
        :key="
          insuredKey + newAnswers.startDate + healthStore.getBranchConfiguration().hiringMaxAges
        "
        ref="$insuredInfoComponent"
        :answers="newAnswers"
        :configurations="props.configurations"
        :useTooOldGreaterOrEqual="true"
        :cp-validation-rules="[isNotFromNavarra]"
        :showTitle="false"
      />

      <div class="actions">
        <BaseButton @click="submit">
          {{ t(`rater.base.${isModify ? 'recalculate-submit' : 'submit'}`) }}
        </BaseButton>

        <BaseButton
          v-if="isModify"
          type="transparent"
          @click="emit('close')"
        >
          {{ t('footer.exit') }}
        </BaseButton>
      </div>
    </v-form>
  </div>
</template>

<script setup lang="ts">
  import { computed, onBeforeMount, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'

  import ForWhomSelectorComponent from '@/modules/core/components/formFields/ForWhomSelectorComponent.vue'
  import BaseButton from '@/modules/core/components/ui/BaseButton.vue'
  import DatePickerComponent from '@/modules/core/components/ui/DatePickerComponent.vue'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import { ForWhomEnum, UserRoleEnum } from '@/modules/core/models/enums'
  import { useLoadingStore } from '@/modules/core/stores/loadingStore'
  import { useHealthStore } from '@/modules/health/rater/stores'

  import { addDaysToDate } from '@/utils/date.utils'

  import CoverageRadioGroup from '@/modules/common/common/components/coverages/CoverageRadioGroup.vue'
  import InsuredInfoComponent from '@/modules/common/common/components/panels/InsuredInfoComponent.vue'
  import type CoverageBasic from '@/modules/common/common/models/APP/CoverageBasic'
  import Insured from '@/modules/common/common/models/APP/Insured'
  import AddressDetails from '@/modules/common/hiring/models/APP/AddressDetails'
  import BaseSelect from '@/modules/core/components/ui/BaseSelect.vue'
  import HealthConfigurations from '@/modules/health/common/models/APP/HealthConfigurations'
  import HealthRaterAnswers from '@/modules/health/rater/models/APP/HealthRaterAnswers'

  const { t } = useI18n()
  const loadingStore = useLoadingStore()
  const healthStore = useHealthStore()
  const { required, isNotFromNavarra } = useRules()

  const emit = defineEmits(['submit', 'close'])

  interface PropsInterface {
    answers: Record<string, any>
    configurations: HealthConfigurations
    isModify: boolean
  }
  const props = withDefaults(defineProps<PropsInterface>(), {
    isModify: true
  })

  const $form = ref()
  const newAnswers = ref(new HealthRaterAnswers(props.answers))
  const forWhom = ref(newAnswers.value.forWhom)
  const forWhomPeople = ref(newAnswers.value.forWhomPeople)
  const insuranceType = ref(props.answers.insuranceType)
  const insuranceOptions = ref(getInitInsuranceOptions())
  const abroadType = ref(props.answers.abroadOption)
  const abroadOptions = ref(getInitAbroadOptions())
  const coverage = ref(props.answers.coverage)
  const coverageOptions = ref<CoverageBasic[]>([])
  const $insuredInfoComponent = ref()

  const isPymes = computed(() => props.configurations.isPymes)
  const insuredKey = computed(() => forWhom.value ?? '' + forWhomPeople.value)
  const minDateInit = computed(() => {
    const tomorrow = addDaysToDate(1, new Date())
    tomorrow.setHours(0, 0, 0, 0)
    return tomorrow
  })
  const maxDateInit = computed(() =>
    props.configurations.maxDays ? addDaysToDate(props.configurations.maxDays) : null
  )

  function getInitInsuranceOptions() {
    return props.configurations.insuranceTypesOptions.filter((i: any) => !i.disabled)
  }

  function getInitAbroadOptions() {
    return props.configurations.stayAbroadTypesOptions?.filter((i: any) => !i.disabled) || []
  }

  function initialize() {
    newAnswers.value.startDate = props.answers.startDate ?? addDaysToDate(1)
    newAnswers.value.insuranceType =
      props.answers.insuranceType ?? props.configurations.defaultInsuranceType
    newAnswers.value.abroadOption = props.answers.abroadOption ?? '1'
    newAnswers.value.coverage = props.answers.coverage ?? null
  }

  async function submit() {
    const { valid } = await $form.value.validate()
    if (!valid) return

    if (insuranceType.value !== '3') {
      abroadType.value = null
      coverage.value = null
    }

    const { insureds, policyHolderPostal } = $insuredInfoComponent.value!.submit()

    newAnswers.value.updateAttributes({ insureds })
    if (props.isModify) {
      newAnswers.value.insuranceType = insuranceType.value
      newAnswers.value.abroadOption = abroadType.value
    }

    newAnswers.value.coverage = coverage.value
    newAnswers.value.policyHolderPostal = policyHolderPostal
    newAnswers.value.updateInsuredAttributes(0, insureds[0])

    emit('submit', newAnswers.value)
  }

  async function beforeMount() {
    try {
      loadingStore.startLoading()
      initialize()
      if (!isPymes.value) coverageOptions.value = await healthStore.loadCoverages({})
    } catch (error) {
      // console.log(error);
    } finally {
      loadingStore.stopLoading()
    }
  }

  onBeforeMount(beforeMount)
  watch(forWhom, (newForWhom) => {
    if (newForWhom === ForWhomEnum.NOT_ME && newAnswers.value.insureds.length > 0) {
      newAnswers.value.insureds[0].role = UserRoleEnum.INSURED
    }
    if (newForWhom !== ForWhomEnum.NOT_ME && newAnswers.value.insureds.length > 0) {
      newAnswers.value.insureds[0].role = UserRoleEnum.HOLDER_INSURED
    }
    newAnswers.value.forWhom = newForWhom
  })
  watch(forWhomPeople, (newForWhomPeople, prevForWhomPeople) => {
    if (newForWhomPeople > prevForWhomPeople) {
      for (let i = prevForWhomPeople; i < newForWhomPeople; i++) {
        newAnswers.value.insureds.push(
          new Insured({
            role: UserRoleEnum.INSURED,
            riskNumber: i,
            addressDetails: new AddressDetails(newAnswers.value.insureds[0].addressDetails)
          })
        )
      }
    }
    if (newForWhomPeople < prevForWhomPeople) {
      newAnswers.value.insureds = newAnswers.value.insureds.slice(
        0,
        newForWhomPeople - prevForWhomPeople
      )
    }
    newAnswers.value.forWhomPeople = newForWhomPeople
  })
  watch(insuranceType, async (newInsuranceType: string) => {
    await healthStore.loadMaxAgeByBusinessLine({
      insuranceType: newInsuranceType,
      abroadOption: abroadType.value
    })
    abroadType.value = null
  })
  watch(abroadType, async (newAbroadType: string) => {
    await healthStore.loadMaxAgeByBusinessLine({
      insuranceType: insuranceType.value,
      abroadOption: newAbroadType
    })
  })
</script>

<style lang="scss" scoped>
  .ModifyInsuranceModal {
    width: $size-full;
    max-width: $size-375;
    margin: $spacing-l auto;
  }
</style>
